// Cube-recompute core, shared by the CLI (demo_scripts/setup_04_aggregate.js) and the web
// backend (the ingest job). Reads back the source's private record containers
// (a source now projects into more than one — diversity/, operations/, …),
// computes the flat qb:Observation aggregates declared in aggregations.json,
// applies small-cell suppression, and PUTs one Turtle file per aggregate to
// /{inst}/diversity/aggregates/. Full recompute each run (cheap at this scale).
//
// `period` is a global dimension appended to every aggregate. Each aggregate
// carries its dataset's small-number rules (lib/datasets.js withDatasets):
// `suppressBelow` gates contributing records; a sum cube may additionally gate
// its summed value with `suppressMeasureBelow`. A cube with one declared
// dimension follows `minPerColumn` instead, when its dataset sets one: per
// period, it is shared whole when at least that many records have a value in
// its column, and withheld whole when fewer do. The distribution of one column
// carries far less risk than a cross, as long as enough records make it up;
// docs/ref-belonging-plan.md, "Small numbers". An aggregate
// naturally matches only the record type(s) carrying all its dimensions, so a
// gender×nationality aggregate reads diversity records and a diet aggregate reads
// operations records with no per-aggregate container wiring.
//
// It writes ONLY the private aggregates container — never shared/.
// Surfacing a new period to partners is a separate governance act (republish).

import { prefixMap, turtlePrefixLines } from './prefixes.js';

const CONCURRENCY = 24;

const slugify = (s) => String(s).replace(/[^0-9A-Za-z]+/g, '-').replace(/^-|-$/g, '');
const sourceComponent = (value) => String(value).toLowerCase()
  .replace(/[^0-9a-z]+/g, '-').replace(/^-+|-+$/g, '');

// Which source wrote which record class, as its batch files are named
// (<source>--<class>--batch-N.ttl). The dashboard summary counts records per
// kind so that a dataset can add up its own.
export const recordKind = (sourceId, type) => `${sourceComponent(sourceId) || 'records'}--${sourceComponent(type) || 'records'}`;
const kindOfFile = (url) => {
  const [kind, batch] = url.split('/').at(-1).split('--batch-');
  return batch === undefined ? null : kind;
};

function parseSubjectBody(body) {
  const out = {};
  for (const stmt of body.split(';')) {
    const s = stmt.trim().replace(/\s*\.\s*$/, '');
    if (!s) continue;
    const sp = s.indexOf(' ');
    const pred = s.slice(0, sp).trim();
    const objsRaw = s.slice(sp + 1).trim();
    if (!pred || pred === 'a') continue;
    const objs = splitObjects(objsRaw).map((raw) => ({ raw, norm: normObject(raw) }));
    (out[pred] ??= []).push(...objs);
  }
  return out;
}

// Parse every generated record subject in either a legacy one-record document
// or a batch. The raw token is retained for verbatim cube re-emission.
export function parseRecordParts(ttl) {
  const parts = [];
  const pattern = /<#([^>]+)>\s+([\s\S]*?)\s*\.\s*(?=<#|$)/g;
  for (const match of ttl.matchAll(pattern)) {
    const record = parseSubjectBody(match[2]);
    const sourceId = record['core:sourceId']?.[0]?.norm ?? null;
    parts.push({ subject: match[1], sourceId, record });
  }
  return parts;
}

export function parseRecord(ttl) {
  return parseRecordParts(ttl)[0]?.record ?? {};
}

export function mergeRecordParts(parts) {
  const merged = {};
  for (const part of parts) {
    for (const [predicate, values] of Object.entries(part)) {
      const existing = merged[predicate] ??= [];
      for (const value of values) {
        if (!existing.some((v) => v.raw === value.raw)) existing.push(value);
      }
    }
  }
  return merged;
}

// split a comma-separated object list, respecting quoted literals
function splitObjects(s) {
  const parts = []; let cur = '', q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"' && s[i - 1] !== '\\') q = !q;
    if (c === ',' && !q) { parts.push(cur.trim()); cur = ''; }
    else cur += c;
  }
  if (cur.trim()) parts.push(cur.trim());
  return parts;
}

function normObject(raw) {
  if (raw.startsWith('"')) {
    const m = raw.match(/^"((?:[^"\\]|\\.)*)"/);
    return m ? m[1] : raw;
  }
  return raw; // prefixed IRI, e.g. adr:Female
}

function cartesian(lists) {
  return lists.reduce((acc, list) => acc.flatMap((a) => list.map((x) => [...a, x])), [[]]);
}

// The column minimum a cube follows, or null when it uses cell suppression:
// only a cube with one declared dimension (the period is not counted) can.
export function minPerColumnOf(a) {
  if (a.dimensions?.length !== 1) return null;
  return a.minPerColumn ?? null;
}

// The privacy rule in words, for screens that state it next to a set of
// cubes: a dataset's rules ({ suppressBelow, minPerColumn }).
export function hidingRule(rules) {
  const cells = `hiding counts under ${rules.suppressBelow}`;
  return rules.minPerColumn == null ? cells
    : `single columns shared whole from ${rules.minPerColumn} records, crosses ${cells}`;
}

export function calculateAggregates(records, agg) {
  // A cube counted without its dataset's rules would hide nothing.
  const unruled = agg.aggregates.find((a) => !Number.isInteger(a.suppressBelow));
  if (unruled) throw new Error(`${unruled.id} has no small-number rules: count it with its dataset's (withDatasets).`);
  // Every period the records have. A column nobody answered in one of them
  // has fewer than its minimum there (none), and is withheld like any other.
  const recordPeriods = agg.period
    ? [...new Set(records.flatMap((r) => (r[agg.period] ?? []).map((v) => v.norm)))] : [];
  return agg.aggregates.map((a) => {
    const minPerColumn = minPerColumnOf(a);
    const slice = { dataSet: a.id, dimensions: agg.period ? [...a.dimensions, agg.period] : a.dimensions,
      dataset: a.dataset ?? null, suppressBelow: minPerColumn === null ? a.suppressBelow : 0, minPerColumn,
      suppressMeasureBelow: a.suppressMeasureBelow ?? 0,
      measure: a.measure ?? agg.measure, reducer: a.reducer ?? 'count' };
    const cells = new Map();
    // Records with a value in the cube's column, per period ('' without one).
    const perPeriod = new Map(minPerColumn === null ? [] : recordPeriods.map((p) => [p, 0]));
    const diagnostics = {
      totalRecords: records.length, includedRecords: 0, excludedRecords: 0,
      invalidMeasures: { missing: 0, repeated: 0, nonNumeric: 0, negative: 0 },
    };
    for (const rec of records) {
      const dimVals = slice.dimensions.map((d) => rec[d] ?? []);
      if (dimVals.some((v) => v.length === 0)) { diagnostics.excludedRecords++; continue; }
      let increment = 1;
      if (slice.reducer === 'sum') {
        const measures = rec[slice.measure] ?? [];
        let reason = null;
        if (!measures.length) reason = 'missing';
        else if (measures.length !== 1) reason = 'repeated';
        else {
          const lexical = String(measures[0].norm).trim();
          increment = Number(lexical);
          if (!lexical || !Number.isFinite(increment)) reason = 'nonNumeric';
          else if (increment < 0) reason = 'negative';
        }
        if (reason) {
          diagnostics.invalidMeasures[reason]++;
          diagnostics.excludedRecords++;
          continue;
        }
      }
      diagnostics.includedRecords++;
      for (const p of new Set(agg.period ? dimVals.at(-1).map((v) => v.norm) : [''])) {
        perPeriod.set(p, (perPeriod.get(p) ?? 0) + 1);
      }
      for (const combo of cartesian(dimVals)) {
        const key = combo.map((c) => c.norm).join(' | ');
        const cell = cells.get(key) ?? {
          norms: combo.map((c) => c.norm), raws: combo.map((c) => c.raw), count: 0, contributions: 0,
        };
        cell.count += increment;
        cell.contributions++;
        cells.set(key, cell);
      }
    }
    const all = [...cells.values()];
    const periods = minPerColumn === null ? null : [...perPeriod].sort(([x], [y]) => x.localeCompare(y))
      .map(([period, n]) => ({ period: period || null, records: n, withheld: n < minPerColumn }));
    const withheld = new Set((periods ?? []).filter((p) => p.withheld).map((p) => p.period ?? ''));
    if (periods) slice.withheldPeriods = periods.filter((p) => p.withheld && p.period).map((p) => p.period);
    const periodOf = (c) => (agg.period ? c.norms.at(-1) : '');
    const kept = all.filter((c) => c.contributions >= slice.suppressBelow && !withheld.has(periodOf(c))
      && c.count >= slice.suppressMeasureBelow).sort((a, b) => b.count - a.count);
    return { slice, all, kept, diagnostics, periods };
  });
}

// What one aggregate costs, in the terms the wireframe asks for: how many groups
// the data could produce, how many survive the threshold, and what that loss
// means. The percentage on its own does not carry the judgement — a cube where
// every published group is safe can still say almost nothing — so the verdict is
// computed here, once, and shown wherever a preview is.
//
// A cube that follows minPerColumn is judged per period instead: shared whole,
// or withheld whole, and the verdict says which periods are which.
export function summarizeCells(all, kept, { minPerColumn = null, periods = null } = {}) {
  const possible = all.length;
  const published = kept.length;
  const hidden = possible - published;
  const lossPct = possible ? Math.round((hidden / possible) * 100) : 0;
  const examples = kept.slice(0, 3).map((c) => ({ label: c.norms.join(' · '), count: c.count }));
  if (minPerColumn === null) return { possible, hidden, published, lossPct, examples, ...verdictFor(published, lossPct) };
  return { possible, hidden, published, lossPct, examples, minPerColumn, periods, ...columnVerdictFor(minPerColumn, periods) };
}

function columnVerdictFor(min, periods) {
  const withheld = periods.filter((p) => p.withheld);
  const name = (p) => p.period ?? 'the cube';
  if (!periods.length) return { variant: 'danger', verdict: 'Withheld — no record has a value in this column.' };
  if (withheld.length === periods.length) {
    return { variant: 'danger', verdict: `Withheld — fewer than ${min} records have a value in this column.` };
  }
  if (withheld.length) {
    const list = withheld.map((p) => `${name(p)} (${p.records})`).join(', ');
    return { variant: 'warning', verdict: `Shared whole, except ${list}: fewer than ${min} records there.` };
  }
  return { variant: 'success', verdict: `Shared whole — at least ${min} records have a value in this column.` };
}

function verdictFor(published, lossPct) {
  if (!published) return { variant: 'danger', verdict: 'Nothing survives — every group is under the threshold.' };
  if (lossPct >= 50) return { variant: 'warning', verdict: `Safe, but ${lossPct}% is hidden — the breakdown says little.` };
  if (lossPct >= 20) return { variant: 'info', verdict: `Safe; ${lossPct}% is hidden.` };
  return { variant: 'success', verdict: lossPct ? `Safe — only ${lossPct}% is hidden.` : 'Safe — nothing is hidden.' };
}

// Summarise a whole aggregate list against already-projected records, keyed by
// aggregate id. Keyed, not positional: the caller's list and the summary list
// drift apart the moment anything is added or removed.
export function summarizeAggregates(records, agg) {
  const out = {};
  for (const { slice, all, kept, diagnostics, periods } of calculateAggregates(records, agg)) {
    out[slice.dataSet] = { ...summarizeCells(all, kept, { minPerColumn: slice.minPerColumn, periods }), ...diagnostics,
      measure: slice.measure, reducer: slice.reducer };
  }
  return out;
}

async function runPool(items, worker) {
  let i = 0; const results = new Array(items.length);
  async function next() {
    while (i < items.length) { const idx = i++; results[idx] = await worker(items[idx]); }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, next));
  return results;
}

// A cube under minPerColumn says so on its dataset, with the periods it
// withholds. That is for its owner: publishing moves both into the manifest
// and shares the cube bare (lib/manifest.js sharedCube).
export function emitSlice(slice, cells, publisher, prefixes = prefixMap()) {
  const rule = slice.minPerColumn == null ? ''
    : ` ; core:minPerColumn ${slice.minPerColumn}${(slice.withheldPeriods ?? []).length
      ? ` ; core:withheldPeriod ${slice.withheldPeriods.map((p) => JSON.stringify(p)).join(', ')}` : ''}`;
  const lines = [`<#${slice.dataSet}> a qb:DataSet${rule} .`, ''];
  let idx = 0;
  for (const cell of cells) {
    const id = `obs-${slugify(slice.dataSet)}-${cell.norms.map(slugify).join('-')}-${idx++}`;
    const triples = [`qb:dataSet <#${slice.dataSet}>`, `dcterms:publisher "${publisher}"`];
    slice.dimensions.forEach((dim, d) => triples.push(`${dim} ${cell.raws[d]}`));
    triples.push(`${slice.measure} ${cell.count}`);
    lines.push(`<#${id}>\n  a qb:Observation ;\n  ${triples.join(' ;\n  ')} .`, '');
  }
  const body = lines.join('\n') + '\n';
  return `${turtlePrefixLines(body, prefixes).join('\n')}\n\n${body}`;
}

export const podBaseOf = (connector) => connector.podBase.replace(/\/$/, '') + '/';
export const DEFAULT_AGGREGATES_CONTAINER = 'diversity/aggregates/';
export const aggregatesContainerOf = (connector, agg = {}) =>
  `${podBaseOf(connector)}${agg.container ?? DEFAULT_AGGREGATES_CONTAINER}`;
// What is shared with a partner sits next to the aggregates it is copied from:
// belonging/aggregates/ → belonging/shared/<partner>/ (without a partner, the
// shared/ container itself). Works on a Pod-relative path or a full URL alike.
export const sharedContainerOf = (aggregatesContainer, partner = null) =>
  `${aggregatesContainer.replace(/aggregates\/$/, '')}shared/${partner ? `${partner}/` : ''}`;

// Read the source's private records back out of the Pod and merge the sibling
// parts of each one into a single record. Separated from recompute because
// previewing an aggregate needs exactly this and must write nothing: the read is
// the expensive half, and it is the half that is safe to repeat.
export async function readProjectedRecords({ connector, recordContainers, authFetch, period = 'core:period' }) {
  const podBase = podBaseOf(connector);
  // Every record-type container the sources project into (their mappings say
  // which); none means there is nothing to read.
  const containers = (recordContainers ?? [])
    .map((entry) => typeof entry === 'string'
      ? { sourceId: null, url: `${podBase}${entry}` }
      : { sourceId: entry.sourceId, url: `${podBase}${entry.container}` });

  // discover records across every container via ldp:contains
  const recordUrls = [];
  const seen = new Set();
  for (const container of containers) {
    const listRes = await authFetch(container.url, { headers: { Accept: 'text/turtle' } });
    const listBody = await listRes.text();
    for (const m of listBody.matchAll(/<([^>]*\.ttl)>/g)) {
      const href = new URL(m[1], container.url).href;
      const name = href.split('/').at(-1);
      if (container.sourceId && name.includes('--batch-')
        && !name.startsWith(`${sourceComponent(container.sourceId)}--`)) continue;
      const identity = `${container.sourceId ?? ''}\u0000${href}`;
      if (!seen.has(identity)) { seen.add(identity); recordUrls.push({ href, sourceId: container.sourceId }); }
    }
  }

  const parsedDocuments = await runPool(recordUrls, async ({ href, sourceId }) => {
    const res = await authFetch(href, { headers: { Accept: 'text/turtle' } });
    const ttl = await res.text();
    return parseRecordParts(ttl).map((part) => ({
      kind: kindOfFile(href),
      key: `${sourceId ?? ''}\u0000${part.sourceId ?? href.split('/').at(-1)}`,
      identity: `${sourceId ?? ''}\u0000${href.replace(/[^/]+$/, '')}\u0000${part.subject}`,
      ownerSourceId: sourceId,
      record: part.record,
    }));
  });
  const byKey = new Map();
  const identities = new Set();
  const sourceStats = {};
  for (const { href, sourceId } of recordUrls) {
    if (!sourceId) continue;
    const stats = sourceStats[sourceId] ??= { records: 0, documents: 0 };
    stats.documents++;
  }
  let recordCount = 0;
  const kindStats = {};
  for (const part of parsedDocuments.flat()) {
    if (identities.has(part.identity)) throw new Error(`Duplicate projected record identity: ${part.identity.split('\u0000').at(-1)}`);
    identities.add(part.identity);
    recordCount++;
    if (part.kind) {
      const stats = kindStats[part.kind] ??= { records: 0, periods: [] };
      stats.records++;
      for (const { norm } of part.record[period] ?? []) if (norm && !stats.periods.includes(norm)) stats.periods.push(norm);
    }
    if (part.ownerSourceId) (sourceStats[part.ownerSourceId] ??= { records: 0, documents: 0 }).records++;
    (byKey.get(part.key) ?? byKey.set(part.key, []).get(part.key)).push(part.record);
  }
  const records = [...byKey.values()].map(mergeRecordParts);
  const periods = [...new Set(records.flatMap((record) =>
    (record[period] ?? []).map((value) => value.norm)).filter(Boolean))].sort();
  for (const stats of Object.values(kindStats)) stats.periods.sort();
  return { records, recordUrls, sourceStats, kindStats, periods,
    documentCount: recordUrls.length, recordCount };
}

// Recompute the private aggregate cube. Returns document, RDF-part, and merged
// logical-row counts plus per-slice results so callers never equate files with
// records.
export async function recomputeAggregates({ connector, agg, recordContainers, authFetch, onProgress,
  publisher = connector.label, prefixes = prefixMap() }) {
  const aggregatesContainer = aggregatesContainerOf(connector, agg);
  const { records: parsed, documentCount, recordCount, sourceStats, kindStats, periods } =
    await readProjectedRecords({ connector, recordContainers, authFetch, period: agg.period ?? undefined });

  // ensure aggregates container exists
  await authFetch(aggregatesContainer, { method: 'PUT', headers: { 'Content-Type': 'text/turtle' }, body: '' });

  // Flatten the aggregate list. `period` is a global dimension appended to each;
  // each cube's thresholds are its dataset's. Every marginal is suppressed
  // independently. A record that lacks any of an aggregate's dimensions is
  // simply not counted for it, so aggregates route to the right record type.
  const calculated = calculateAggregates(parsed, agg);

  const summary = [];
  let sliceIdx = 0;
  for (const { slice, all, kept, diagnostics, periods } of calculated) {
    const ttl = emitSlice(slice, kept, publisher, prefixes);
    const res = await authFetch(`${aggregatesContainer}${slice.dataSet}.ttl`, {
      method: 'PUT', headers: { 'Content-Type': 'text/turtle' }, body: ttl,
    });
    summary.push({ dataset: slice.dataset, dataSet: slice.dataSet, measure: slice.measure, reducer: slice.reducer,
      cells: all.length, kept: kept.length, suppressed: all.length - kept.length,
      ...(periods ? { minPerColumn: slice.minPerColumn,
        withheldPeriods: periods.filter((p) => p.withheld).map((p) => p.period) } : {}),
      ...diagnostics, status: res.status });
    onProgress?.({ done: ++sliceIdx, total: calculated.length, message: 'Recomputing aggregates' });
  }

  return { recordCount, documentCount, logicalRecordCount: parsed.length, sourceStats, kindStats, periods,
    aggregatesContainer, slices: summary };
}
